import PropTypes from 'prop-types';
import React, { Component } from 'react';

import DateWrapper from '../../common/DateWrapper';
import DataAid from '../constants/data-aids';
import { UX2 } from '@wsb/guac-widget-core';

import getTimeZoneOffset from '../getTimeZoneOffset';

const DAY_FORMAT = DateWrapper.WEEKDAY;
const MONTH_ONLY_FORMAT = DateWrapper.MONTH;
const DATE_ONLY_FORMAT = DateWrapper.DAY_ORDINAL;
const DATE_ONLY_NON_ORDINAL_FORMAT = DateWrapper.DAY;
// eslint-disable-next-line no-bitwise
const MONTH_AND_DATE_FORMAT = MONTH_ONLY_FORMAT | DATE_ONLY_FORMAT;
// eslint-disable-next-line no-bitwise
const MONTH_AND_DATE_FORMAT_INVERSE = DATE_ONLY_NON_ORDINAL_FORMAT | MONTH_ONLY_FORMAT;
const SEPARATOR = '-';
const inverseDateFormatLocales = [
  'fr-FR',
  'en-AU',
  'en-UK',
  'en-HK',
  'en-IE',
  'en-IL',
  'en-IN',
  'en-MY',
  'en-NZ',
  'en-PK',
  'en-SG',
  'en-ZA'
];

class EventDate extends Component {
  static get propTypes() {
    return {
      locale: PropTypes.string,
      start: PropTypes.string.isRequired,
      end: PropTypes.string.isRequired,
      smallScreen: PropTypes.bool,
      isManual: PropTypes.bool.isRequired,
      order: PropTypes.number.isRequired,
      date: PropTypes.string,
      timezone: PropTypes.shape({ offset: PropTypes.number, zone: PropTypes.string })
    };
  }

  constructor(props) {
    super(props);
    const { locale } = this.props;

    DateWrapper.locale(locale);
    const start = new DateWrapper(this.props.start);
    const end = new DateWrapper(this.props.end);
    this.state = {
      start,
      end,
      sameDayEvent: start.isSame(end, 'day'),
      sameMonthEvent: start.isSame(end, 'month')
    };
  }

  renderDays() {
    const { start, end } = this.state;
    const { locale, isManual, timezone } = this.props;

    if (isManual) {
      return null;
    }

    if (timezone) {
      start.set('timeZoneOffset', timezone.offset);
      end.set('timeZoneOffset', timezone.offset);
    } else {
      start.set('timeZoneOffset', getTimeZoneOffset(this.props.start));
      end.set('timeZoneOffset', getTimeZoneOffset(this.props.end));
    }
    start.locale(locale);
    end.locale(locale);

    return (
      <UX2.Element.DisplayHeading.Sub tag='div' data-aid={ DataAid.CALENDAR_EVENT_DAY }>
        { this.state.sameDayEvent
          ? start.toLocaleString(DAY_FORMAT)
          : `${start.toLocaleString(DAY_FORMAT)} ${SEPARATOR} ${end.toLocaleString(DAY_FORMAT)}` }
      </UX2.Element.DisplayHeading.Sub>
    );
  }

  renderDate() {
    const { start, end, sameDayEvent, sameMonthEvent } = this.state;
    const { smallScreen, locale, date, isManual, order } = this.props;
    const inverseDateFormat = inverseDateFormatLocales.includes(locale);
    const monthDateFormatStart = inverseDateFormat
      ? start.toLocaleString(MONTH_AND_DATE_FORMAT_INVERSE)
      : start.toLocaleString(MONTH_AND_DATE_FORMAT);
    const monthDateFormatEnd = inverseDateFormat
      ? end.toLocaleString(MONTH_AND_DATE_FORMAT_INVERSE)
      : end.toLocaleString(MONTH_AND_DATE_FORMAT);
    const dataFormat = locale.match(/en/) ? DATE_ONLY_FORMAT : DATE_ONLY_NON_ORDINAL_FORMAT; // temporary fix until we get date ordinal translated for i18n
    const monthDateOrdering = inverseDateFormat
      ? [dataFormat, MONTH_ONLY_FORMAT]
      : [MONTH_ONLY_FORMAT, dataFormat];
    const monthDateBreakSeparated = (
      <span>
        { start.toLocaleString(monthDateOrdering[0]) }
        <br />
        { start.toLocaleString(monthDateOrdering[1]) }
      </span>
    );
    const routeProps = isManual
      ? {
        'data-field-id': 'events.date',
        'data-field-route': `/events/${order}`
      }
      : {};

    if (isManual) {
      return <UX2.Element.DisplayHeading { ...routeProps }>{ date }</UX2.Element.DisplayHeading>;
    }

    if (sameDayEvent) {
      return smallScreen ? (
        <UX2.Element.DisplayHeading { ...routeProps }>
          { monthDateFormatStart }
        </UX2.Element.DisplayHeading>
      ) : (
        <UX2.Element.DisplayHeading { ...routeProps }>
          { monthDateBreakSeparated }
        </UX2.Element.DisplayHeading>
      );
    } else if (sameMonthEvent) {
      return smallScreen ? (
        <UX2.Element.DisplayHeading { ...routeProps }>
          { monthDateFormatStart + SEPARATOR + end.toLocaleString(DATE_ONLY_FORMAT) }
        </UX2.Element.DisplayHeading>
      ) : (
        <UX2.Element.DisplayHeading { ...routeProps }>
          { start.toLocaleString(MONTH_ONLY_FORMAT) }
          <br />
          { start.toLocaleString(DATE_ONLY_FORMAT) +
            SEPARATOR +
            end.toLocaleString(DATE_ONLY_FORMAT) }
        </UX2.Element.DisplayHeading>
      );
    }
    return (
      <UX2.Element.DisplayHeading { ...routeProps }>
        { monthDateFormatStart + SEPARATOR }
        <br />
        { monthDateFormatEnd }
      </UX2.Element.DisplayHeading>
    );
  }

  render() {
    return (
      <UX2.Element.Block>
        { this.renderDays() }
        <UX2.Element.Block data-aid={ DataAid.CALENDAR_EVENT_DATE } children={ this.renderDate() } />
      </UX2.Element.Block>
    );
  }
}

export default EventDate;
